import React, { useCallback, useEffect, useState } from 'react';
import { Camera, CheckCircle2, Clock, Loader2, Send, Truck, XCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { adminService } from '../../../services/adminService';
import { apiErrorMessage } from '../../../lib/apiError';
import { PhotoProofGallery } from '../../../components/verification/PhotoProofGallery';
import { LocationLine } from '../../../components/LocationLine';

// The panel is only meaningful once the kitchen has started on the order.
const PROOF_STATUSES = new Set([
  'in_production', 'ready_for_delivery', 'out_for_delivery', 'delivered', 'failed_delivery',
]);

const OUTCOME = {
  delivered: { icon: CheckCircle2, cls: 'bg-green-100 text-green-800', label: 'Delivered' },
  failed: { icon: XCircle, cls: 'bg-orange-100 text-orange-800', label: 'Delivery failed' },
  pending: { icon: Clock, cls: 'bg-blue-100 text-blue-800', label: 'Not yet delivered' },
};

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

/**
 * Delivery Proof — everything an admin needs to settle a dispute in one place:
 * both photo sets with who/when/download, the driver's pickup count, the
 * outcome and its notes, and a button to send the proof to the client.
 */
export const DeliveryProofSection = ({ orderId, orderStatus, active }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const eligible = PROOF_STATUSES.has(orderStatus);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      setData(await adminService.getOrderDeliveryProof(orderId));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (active && eligible) load();
  }, [active, eligible, load]);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await adminService.sendOrderDeliveryProof(orderId, message.trim());
      toast.success(
        `Proof sent to ${res.recipients.length} recipient${res.recipients.length === 1 ? '' : 's'}`
      );
      setSendOpen(false);
      setMessage('');
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to send proof'));
    } finally {
      setSending(false);
    }
  };

  if (!eligible) return null;

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 py-4" data-testid="delivery-proof-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading delivery proof…
      </div>
    );
  }
  if (!data) return null;

  const outcome = OUTCOME[data.outcome] || OUTCOME.pending;
  const OutcomeIcon = outcome.icon;
  const changedLines = (data.pickup_reconciliation?.lines || []).filter((l) => l.delta !== 0);
  const lastSend = data.proof_sends?.[data.proof_sends.length - 1];

  return (
    <div className="border rounded-lg p-4 space-y-4" data-testid="delivery-proof-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
            <Camera className="h-4 w-4 text-amber-600" /> Delivery Proof
          </p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <Badge className={`${outcome.cls} text-xs`} data-testid="proof-outcome-badge">
              <OutcomeIcon className="h-3 w-3 mr-1" />
              {outcome.label}
            </Badge>
            {data.outcome_at && (
              <span className="text-xs text-gray-500" data-testid="proof-outcome-at">{when(data.outcome_at)}</span>
            )}
            {data.proof_sends?.length > 0 && (
              <Badge variant="outline" className="text-xs text-green-700 border-green-300" data-testid="proof-sent-badge">
                Proof sent {data.proof_sends.length}×
              </Badge>
            )}
          </div>
        </div>
        <Button
          size="sm"
          className="bg-amber-500 hover:bg-amber-600 shrink-0"
          disabled={!data.has_photos}
          onClick={() => setSendOpen(true)}
          data-testid="send-proof-btn"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          Send proof to client
        </Button>
      </div>

      <LocationLine
        name={data.location_name}
        source={data.location_name_source}
        testId="proof-location"
      />

      {(data.driver_name || data.route_number) && (
        <p className="text-xs text-gray-500 flex items-center gap-1.5 flex-wrap break-all" data-testid="proof-route-line">
          <Truck className="h-3.5 w-3.5 text-gray-400 shrink-0" />
          {data.route_number && <span>Route {data.route_number}</span>}
          {data.driver_name && <span>· Driver {data.driver_name}</span>}
          {data.production_completed_by_name && <span>· Packed by {data.production_completed_by_name}</span>}
        </p>
      )}

      <PhotoProofGallery
        testIdPrefix="admin-proof"
        emptyLabel="No photos have been taken for this order yet"
        groups={[
          {
            key: 'production',
            title: 'Packed in the kitchen',
            caption: 'before it left the bakery',
            photos: data.production_photos || [],
          },
          {
            key: 'delivery',
            title: data.outcome === 'failed' ? 'At the delivery attempt' : "At the client's door",
            caption: 'taken by the driver',
            photos: data.delivery_photos || [],
          },
        ]}
      />

      {data.pickup_reconciliation ? (
        <div data-testid="proof-pickup-count">
          <p className="text-sm font-medium text-gray-800 mb-2">
            Driver&apos;s pickup count
            <span className="ml-2 text-xs font-normal text-gray-400">
              {changedLines.length === 0
                ? 'every line matched the order'
                : `${changedLines.length} line${changedLines.length === 1 ? '' : 's'} changed`}
            </span>
          </p>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="px-2 py-2">Ordered</th>
                  <th className="px-2 py-2">Handed over</th>
                  <th className="text-left px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(data.pickup_reconciliation.lines || []).map((l) => (
                  <tr
                    key={l.product_id}
                    className={l.delta !== 0 ? 'bg-amber-50' : ''}
                    data-testid={`proof-line-${l.product_id}`}
                  >
                    <td className="px-3 py-2 font-medium text-gray-900">{l.product_name}</td>
                    <td className="px-2 py-2 text-center text-gray-600">{l.ordered_qty}</td>
                    <td className="px-2 py-2 text-center font-medium">
                      {l.delivered_qty}
                      {l.delta !== 0 && (
                        <span className={l.delta > 0 ? 'text-purple-600 ml-1' : 'text-orange-600 ml-1'}>
                          ({l.delta > 0 ? '+' : ''}{l.delta})
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">{l.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.pickup_reconciliation.performed_by_name && (
            <p className="text-xs text-gray-400 mt-1.5">
              Counted by {data.pickup_reconciliation.performed_by_name}
              {data.pickup_reconciliation.performed_at ? ` · ${when(data.pickup_reconciliation.performed_at)}` : ''}
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-gray-400" data-testid="proof-no-pickup-count">
          No pickup count was recorded — the invoice bills the ordered quantities.
        </p>
      )}

      {data.delivery_notes && (
        <div className="p-3 rounded-lg bg-gray-50 border text-sm" data-testid="proof-delivery-notes">
          <span className="font-medium text-gray-700">Driver&apos;s notes: </span>
          <span className="text-gray-600">{data.delivery_notes}</span>
        </div>
      )}

      {lastSend && (
        <div className="text-xs text-gray-500 border-t pt-3" data-testid="proof-send-history">
          <p className="font-medium text-gray-600 mb-1">Proof sent to this client</p>
          {data.proof_sends.map((s, i) => (
            <p key={`${s.sent_at}-${i}`} className="truncate">
              {when(s.sent_at)} by {s.sent_by_name || 'admin'} → {s.recipients.join(', ')}
              {s.message ? ` — “${s.message}”` : ''}
            </p>
          ))}
        </div>
      )}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md" data-testid="send-proof-dialog">
          <DialogHeader>
            <DialogTitle>Send proof of delivery</DialogTitle>
            <DialogDescription>
              The client gets an in-app notice and an email with these photos shown inline,
              plus a link to the order. Sent regardless of their email preferences — this
              answers a question they asked.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="proof-message">Message to the client (optional)</Label>
            <Textarea
              id="proof-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={600}
              placeholder="e.g. Here are the photos from Tuesday's drop-off — the boxes were left with your front desk at 6:42am."
              className="mt-1"
              data-testid="proof-message-input"
            />
            <p className="text-xs text-gray-400 mt-1">{message.length}/600</p>
          </div>
          {data.proof_sends?.length > 0 && (
            <p className="text-xs text-amber-600" data-testid="proof-resend-warning">
              Proof was already sent {data.proof_sends.length} time{data.proof_sends.length === 1 ? '' : 's'} —
              the most recent on {when(lastSend.sent_at)}.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleSend}
              disabled={sending}
              data-testid="confirm-send-proof-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send proof
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryProofSection;
